import React, { memo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  FaSolarPanel,
  FaBolt,
  FaTemperatureHigh,
  FaCheckCircle,
  FaExpand,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const imageVariants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 20 : -20,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4 },
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction < 0 ? 20 : -20,
    transition: { duration: 0.4 },
  }),
};

const FeatureChip = memo(({ feature }) => (
  <motion.span
    whileHover={{ scale: 1.05 }}
    className="inline-flex items-center gap-2 bg-gray-800 px-3 py-1.5 rounded-full text-sm text-gray-300"
  >
    <FaCheckCircle className="text-green-400" />
    {feature}
  </motion.span>
));

FeatureChip.displayName = "FeatureChip";

FeatureChip.propTypes = {
  feature: PropTypes.string.isRequired,
};

const DotIndicator = memo(({ active, onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.2 }}
    whileTap={{ scale: 0.9 }}
    className={`w-3 h-3 rounded-full ${active ? "bg-blue-500" : "bg-gray-600 cursor-pointer"}`}
  />
));

DotIndicator.displayName = "DotIndicator";

DotIndicator.propTypes = {
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const PVTHero = ({
  detailData,
  panelImages,
  currentImageIndex,
  nextImage,
  prevImage,
  setImageIndex,
  openImageModal,
}) => {
  const { t } = useTranslation();
  const pvtHeroData = t("components.pvt.pvtHero", { returnObjects: true });

  const [direction, setDirection] = React.useState(0);

  // Move hooks before any conditional logic
  useEffect(() => {
    if (panelImages && panelImages.length > 0) {
      panelImages.forEach((image) => {
        const img = new Image();
        img.src = image.src;
      });
    }
  }, [panelImages]);

  const handlePrev = useCallback(
    (e) => {
      e.stopPropagation();
      setDirection(-1);
      prevImage();
    },
    [prevImage]
  );

  const handleNext = useCallback(
    (e) => {
      e.stopPropagation();
      setDirection(1);
      nextImage();
    },
    [nextImage]
  );

  const handleDotClick = useCallback(
    (index) => {
      setDirection(index > currentImageIndex ? 1 : -1);
      setImageIndex(index);
    },
    [currentImageIndex, setImageIndex]
  );

  const panelData = detailData
    ? {
        name: detailData.panelData.name,
        tagline: detailData.panelData.tagline,
        description: detailData.panelData.description,
      }
    : null;

  if (!panelData || !panelImages || panelImages.length === 0) {
    return null;
  }

  return (
    <motion.div
      className="flex flex-col lg:flex-row items-center mb-16 gap-8"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      id="safePad"
    >
      <div className="lg:w-1/2">
        <div className="inline-flex items-center gap-2 bg-blue-900/30 px-4 py-2 rounded-full mb-4">
          <FaSolarPanel className="text-yellow-400" />
          <span className="text-sm text-blue-200 font-medium">
            {pvtHeroData.badge}
          </span>
        </div>
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
          {panelData.name}
          <span className="block mt-2 text-xl md:text-2xl font-normal text-gray-300">
            {panelData.tagline}
          </span>
        </h1>
        <p className="text-gray-300 mb-6 text-md lg:text-lg">
          {panelData.description}
        </p>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-900/20 p-4 rounded-lg flex items-center gap-3">
            <FaBolt className="text-yellow-400 text-2xl" />
            <div>
              <div className="text-white font-bold text-lg">
                {pvtHeroData.electricalPower.value}
              </div>
              <div className="text-gray-400 text-sm">
                {pvtHeroData.electricalPower.label}
              </div>
            </div>
          </div>
          <div className="bg-red-900/20 p-4 rounded-lg flex items-center gap-3">
            <FaTemperatureHigh className="text-red-400 text-2xl" />
            <div>
              <div className="text-white font-bold text-lg">
                {pvtHeroData.thermalPower.value}
              </div>
              <div className="text-gray-400 text-sm">
                {pvtHeroData.thermalPower.label}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {pvtHeroData.features.map((feature, index) => (
            <FeatureChip key={index} feature={feature} />
          ))}
        </div>
      </div>

      <div className="w-full lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-green-500/20 blur-3xl rounded-full"></div>
        <div className="relative">
          <div className="absolute top-4 left-4 bg-blue-900/80 text-white text-sm font-bold px-4 py-2 rounded-full z-10">
            {pvtHeroData.hybridLabel}
          </div>
          <motion.button
            onClick={openImageModal}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="absolute top-4 right-4 bg-gray-800/80 hover:bg-gray-700/80 text-white p-2.5 rounded-full shadow-lg z-10 cursor-pointer"
          >
            <FaExpand className="text-sm" />
          </motion.button>

          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex justify-between px-4 pointer-events-none z-10">
            <motion.button
              onClick={handlePrev}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-gray-800/80 hover:bg-gray-700/80 text-white p-3 rounded-full shadow-lg pointer-events-auto cursor-pointer"
            >
              <FaArrowLeft className="text-sm" />
            </motion.button>
            <motion.button
              onClick={handleNext}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-gray-800/80 hover:bg-gray-700/80 text-white p-3 rounded-full shadow-lg pointer-events-auto cursor-pointer"
            >
              <FaArrowRight className="text-sm" />
            </motion.button>
          </div>

          <div className="relative w-full h-[450px] sm:h-[500px] md:h-[550px] lg:h-[600px] overflow-hidden rounded-lg border border-gray-800">
            <AnimatePresence
              initial={false}
              custom={direction}
              mode="crossfade"
            >
              <motion.div
                key={currentImageIndex}
                custom={direction}
                variants={imageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="absolute inset-0 w-full h-full"
              >
                <img
                  src={panelImages[currentImageIndex].src}
                  alt={
                    panelImages[currentImageIndex].alt ||
                    pvtHeroData.panelImageAlt
                  }
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={openImageModal}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex justify-center mt-4 gap-2">
            {panelImages.map((_, index) => (
              <DotIndicator
                key={index}
                active={index === currentImageIndex}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>

          <div className="text-center mt-2 text-gray-400 text-sm">
            {panelImages[currentImageIndex].alt || pvtHeroData.panelImageAlt}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default memo(PVTHero);

PVTHero.propTypes = {
  detailData: PropTypes.shape({
    panelData: PropTypes.shape({
      name: PropTypes.string.isRequired,
      tagline: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  panelImages: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ).isRequired,
  currentImageIndex: PropTypes.number.isRequired,
  nextImage: PropTypes.func.isRequired,
  prevImage: PropTypes.func.isRequired,
  setImageIndex: PropTypes.func.isRequired,
  openImageModal: PropTypes.func.isRequired,
};
